import {AbstractControl, FormArray, FormGroup} from '@angular/forms';
import {environment} from 'src/environments/environment';

/**
 * Lightweight debug logger for form/model diagnostics.
 *
 * Enabled only when NOT a production build (`environment.production === false`),
 * so it is active on local/dev/stage and silently no-ops in the release build.
 * Toggle the whole thing from one place by flipping `environment.production`
 * (or change the `enabled` expression below).
 */
const enabled = !environment.production;

// Per-edit form/model logging is debounced: a burst of valueChanges (fast typing,
// or the cascade that markAllFieldsAsTouched triggers on validation) collapses
// into a single log emitted once things go quiet.
const STATE_DEBOUNCE_MS = 300;
const stateTimers: { [component: string]: ReturnType<typeof setTimeout> } = {};
const statePending: { [component: string]: { model: unknown; form: FormGroup } } = {};

interface InvalidControl {
  control: string;
  errors: any;
  value: any;
}

function collectInvalid(root: AbstractControl): InvalidControl[] {
  const result: InvalidControl[] = [];

  const walk = (control: AbstractControl, path: string): void => {
    if (control instanceof FormGroup) {
      Object.keys(control.controls).forEach(key =>
        walk(control.controls[key], path ? `${path}.${key}` : key));
    } else if (control instanceof FormArray) {
      control.controls.forEach((child, index) =>
        walk(child, `${path}[${index}]`));
    } else if (control.invalid) {
      result.push({control: path || '(root)', errors: control.errors, value: control.value});
    }
  };

  walk(root, '');
  return result;
}

export const formLog = {
  enabled,

  /** Generic gated console.log. */
  log(...args: any[]): void {
    if (!enabled) {
      return;
    }
    // tslint:disable-next-line:no-console
    console.log(...args);
  },

  /**
   * Logs the model snapshot, the form value and the current validity.
   * Debounced per component: rapid bursts collapse into one log once quiet.
   */
  state(component: string, model: unknown, form: FormGroup): void {
    if (!enabled) {
      return;
    }
    statePending[component] = {model, form};
    if (stateTimers[component]) {
      clearTimeout(stateTimers[component]);
    }
    stateTimers[component] = setTimeout(() => {
      const pending = statePending[component];
      delete stateTimers[component];
      delete statePending[component];
      if (!pending) {
        return;
      }
      const invalid = collectInvalid(pending.form);
      // tslint:disable-next-line:no-console
      console.groupCollapsed(`[${component}] form/model ${pending.form.valid ? '✅ valid' : '⛔ invalid'}`);
      // tslint:disable-next-line:no-console
      console.log('model :', pending.model);
      // tslint:disable-next-line:no-console
      console.log('form  :', pending.form.getRawValue());
      if (invalid.length) {
        // tslint:disable-next-line:no-console
        console.warn('invalid:', invalid);
      }
      // tslint:disable-next-line:no-console
      console.groupEnd();
    }, STATE_DEBOUNCE_MS);
  },

  /** Logs whether the form is valid and, if not, exactly which controls failed and why. */
  invalid(component: string, form: AbstractControl): void {
    if (!enabled) {
      return;
    }
    if (form.valid) {
      // tslint:disable-next-line:no-console
      console.log(`[${component}] form is VALID`);
      return;
    }
    const invalid = collectInvalid(form);
    // tslint:disable-next-line:no-console
    console.warn(`[${component}] form is INVALID (${invalid.length} control(s)):`, invalid);
  },
};
